import { useState } from "react";
import {
	useEpsonPrinter,
	usePrinterConfig,
} from "@plevands/epson-thermal-printer";

import { Input } from "@/components/atoms/input/input";
import { Button } from "@/components/atoms/button/button";

const DEFAULT_CONFIG = {
	printerIP: "epos-01.laladrillera.pe",
	printerPort: 443,
	useHttps: true,
	deviceId: "local_printer",
	timeout: 3000,
};

export function PrinterSettings() {
	const { config, updateConfig } = usePrinterConfig({
		initialConfig: DEFAULT_CONFIG,
	});

	const { checkConnection } = useEpsonPrinter(config);

	const [printerIP, setPrinterIP] = useState(config?.printerIP);
	const [printerPort, setPrinterPort] = useState(
		config?.printerPort?.toString() || "",
	);
	const [useHttps, setUseHttps] = useState(config?.useHttps);
	const [timeoutValue, setTimeoutValue] = useState(
		config?.timeout?.toString() || "",
	);
	const [isConnected, setIsConnected] = useState<boolean | null>(null);
	const [isChecking, setIsChecking] = useState(false);
	const [saveMessage, setSaveMessage] = useState<string | null>(null);
	const [saveSuccess, setSaveSuccess] = useState(false);

	const handleSave = () => {
		const port = parseInt(printerPort, 10);
		const timeout = parseInt(timeoutValue, 10);

		if (isNaN(port)) {
			setSaveMessage("El puerto debe ser un número válido");
			setSaveSuccess(false);
			return;
		}

		if (isNaN(timeout)) {
			setSaveMessage("El timeout debe ser un número válido");
			setSaveSuccess(false);
			return;
		}

		updateConfig({ printerIP, printerPort: port, useHttps, timeout });

		setSaveMessage("Configuración guardada correctamente");
		setSaveSuccess(true);
		window.setTimeout(() => setSaveMessage(null), 3000);
	};

	const handleTestConnection = async () => {
		setIsChecking(true);
		setIsConnected(null);

		try {
			const { success } = await checkConnection();
			setIsConnected(success);
		} catch {
			setIsConnected(false);
		} finally {
			setIsChecking(false);
		}
	};

	return (
		<div style={{ maxWidth: "600px" }} className="d-flex flex-column gap-3">
			<div className="d-flex flex-column gap-3">
				<div>
					<Input
						label="IP/URL de la impresora"
						type="text"
						value={printerIP}
						onChange={(e) => setPrinterIP(e.target.value)}
						placeholder="epos-01.plevands.edu.pe o 192.168.1.100"
					/>
				</div>

				<div>
					<Input
						label="Puerto"
						type="number"
						value={printerPort}
						onChange={(e) => setPrinterPort(e.target.value)}
						placeholder="443"
						min={1}
						max={65535}
					/>
				</div>

				<div className="form-check">
					<input
						type="checkbox"
						id="useHttps"
						className="form-check-input"
						checked={useHttps}
						onChange={(e) => setUseHttps(e.target.checked)}
					/>
					<label htmlFor="useHttps" className="form-check-label">
						Usar HTTPS
					</label>
				</div>

				<div>
					<Input
						label="Timeout (ms)"
						type="number"
						value={timeoutValue}
						onChange={(e) => setTimeoutValue(e.target.value)}
						placeholder="3000"
						min={1000}
					/>
				</div>
			</div>

			{saveMessage && (
				<div
					className={`alert ${saveSuccess ? "alert-success" : "alert-danger"} py-2`}
					role="alert"
				>
					{saveMessage}
				</div>
			)}

			<div className="d-flex gap-2">
				<Button mode="primary" onClick={handleSave}>
					Guardar configuración
				</Button>
				<Button
					mode="secondary"
					onClick={handleTestConnection}
					disabled={isChecking}
				>
					{isChecking ? (
						<>
							<span
								className="spinner-border spinner-border-sm me-2"
								role="status"
								aria-hidden="true"
							/>
							Probando...
						</>
					) : (
						"Probar conexión"
					)}
				</Button>
			</div>

			{isConnected !== null && (
				<div
					className={`alert ${isConnected ? "alert-success" : "alert-danger"} py-2`}
					role="alert"
				>
					{isConnected
						? "✓ Conexión exitosa con la impresora"
						: "✗ No se pudo conectar con la impresora"}
				</div>
			)}
		</div>
	);
}
